/**
 * PESEL — Polish national identification number. ONE implementation, shared by
 * the backend (apps/backend/src/common/utils/pesel.util.ts re-exports this) and
 * the frontend (imports from '@ip/shared-types'), so the live form validation
 * and the server-side derivation can never drift apart.
 *
 * Format: 11 digits `RRMMDDPPPPK`
 *   RR   — last two digits of the birth year
 *   MM   — birth month PLUS a century offset:
 *            +0  → 1900–1999      +20 → 2000–2099
 *            +40 → 2100–2199      +60 → 2200–2299
 *            +80 → 1800–1899
 *   DD   — day of month
 *   PPPP — serial; its LAST digit encodes sex: odd = mężczyzna, even = kobieta
 *   K    — checksum over the first 10 digits with weights 1,3,7,9,1,3,7,9,1,3:
 *            K = (10 − (Σ dᵢ·wᵢ mod 10)) mod 10
 *
 * The number is the source of truth for date of birth and sex on an employee
 * record: HR types the PESEL, the system derives the rest.
 */

export type Sex = 'male' | 'female';

/** Machine-readable failure reason (the frontend maps these to i18n keys). */
export type PeselErrorCode = 'length' | 'digits' | 'checksum' | 'date';

export interface PeselParseResult {
  valid: boolean;
  /** ISO YYYY-MM-DD; present only when valid. */
  dateOfBirth?: string;
  /** Present only when valid. */
  sex?: Sex;
  /** Polish, user-facing; present only when invalid. */
  error?: string;
  /** Present only when invalid. */
  errorCode?: PeselErrorCode;
}

export const PESEL_WEIGHTS = [1, 3, 7, 9, 1, 3, 7, 9, 1, 3] as const;

/** Polish user-facing message per failure reason. */
export const PESEL_ERROR_MESSAGES: Record<PeselErrorCode, string> = {
  length: 'Nieprawidłowy PESEL (musi mieć 11 cyfr)',
  digits: 'Nieprawidłowy PESEL (dozwolone są wyłącznie cyfry)',
  checksum: 'Nieprawidłowy PESEL (błędna suma kontrolna)',
  date: 'Nieprawidłowy PESEL (nieprawidłowa data urodzenia)',
};

/** Century offset encoded in the month field → the century's first year. */
const CENTURY_BY_MONTH_OFFSET: Record<number, number> = {
  0: 1900,
  20: 2000,
  40: 2100,
  60: 2200,
  80: 1800,
};

function isLeapYear(year: number): boolean {
  return (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;
}

function daysInMonth(year: number, month: number): number {
  const lengths = [31, isLeapYear(year) ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
  return lengths[month - 1];
}

function fail(errorCode: PeselErrorCode): PeselParseResult {
  return { valid: false, errorCode, error: PESEL_ERROR_MESSAGES[errorCode] };
}

/**
 * Parses a PESEL into its date of birth and sex. Pure — no I/O, no Date-now
 * dependency, no throwing.
 */
export function parsePesel(pesel: string): PeselParseResult {
  const raw = typeof pesel === 'string' ? pesel.trim() : '';
  if (raw.length !== 11) return fail('length');
  if (!/^\d{11}$/.test(raw)) return fail('digits');

  const digits = raw.split('').map(Number);

  const sum = PESEL_WEIGHTS.reduce((acc, weight, i) => acc + digits[i] * weight, 0);
  if ((10 - (sum % 10)) % 10 !== digits[10]) return fail('checksum');

  const yy = digits[0] * 10 + digits[1];
  const monthField = digits[2] * 10 + digits[3];
  const day = digits[4] * 10 + digits[5];

  const offset = Math.floor(monthField / 20) * 20;
  const month = monthField - offset;
  const centuryStart = CENTURY_BY_MONTH_OFFSET[offset];
  if (centuryStart === undefined || month < 1 || month > 12) return fail('date');

  const year = centuryStart + yy;
  if (day < 1 || day > daysInMonth(year, month)) return fail('date');

  const dateOfBirth =
    `${String(year).padStart(4, '0')}-` +
    `${String(month).padStart(2, '0')}-` +
    `${String(day).padStart(2, '0')}`;

  // 10th digit (index 9): odd = mężczyzna, even = kobieta.
  const sex: Sex = digits[9] % 2 === 1 ? 'male' : 'female';

  return { valid: true, dateOfBirth, sex };
}

/** Predicate form of {@link parsePesel}. */
export function validatePesel(pesel: string): boolean {
  return parsePesel(pesel).valid;
}
